import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getCurrentUserId, getAccessToken } from "../awsAuth";
import { useAuth } from "./AuthContext";

const LanguageContext = createContext();

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const apiUrl = (path) =>
  path.startsWith("http://") || path.startsWith("https://")
    ? path
    : `${API_BASE || ""}${path}`;

function applyLangToApp(next) {
  const lang = next === "en" ? "en" : "uk";

  try {
    localStorage.setItem("cvanto_lang", lang);
  } catch {}

  try {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  } catch {}

  try {
    if (typeof window !== "undefined" && typeof window.cvantoSetLang === "function") {
      window.cvantoSetLang(lang);
    }
  } catch {}

  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cvanto:set-lang", { detail: { lang } })
      );
    }
  } catch {}

  return lang;
}

export const LanguageProvider = ({ children }) => {
  const { isLoggedIn, userId } = useAuth() || {};
  const [language, setLanguage] = useState("uk");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    try {
      const saved = localStorage.getItem("cvanto_lang");
      const resolved = saved === "en" ? "en" : "uk";
      applyLangToApp(resolved);
      if (alive) setLanguage(resolved);
    } catch {
      const fallback = applyLangToApp("uk");
      if (alive) setLanguage(fallback);
    } finally {
      if (alive) setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadLanguageFromAccount = async () => {
      const uid = userId || getCurrentUserId?.();

      if (!isLoggedIn || !uid) {
        const fallback = applyLangToApp("uk");
        if (!cancelled) setLanguage(fallback);
        return;
      }

      try {
        const token = await getAccessToken?.();

        const res = await fetch(apiUrl(`/api/account/${uid}`), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) return;

        const data = await res.json().catch(() => ({}));
        const serverLang = data?.language;
        const resolved = serverLang === "en" ? "en" : "uk";

        if (cancelled) return;

        applyLangToApp(resolved);
        setLanguage(resolved);
      } catch {}
    };

    loadLanguageFromAccount();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  const setAndPersistLanguage = useCallback(
    async (nextLang) => {
      const resolved = nextLang === "en" ? "en" : "uk";

      setLanguage(resolved);
      applyLangToApp(resolved);

      const uid = userId || getCurrentUserId?.();
      if (!uid) return;

      try {
        const token = await getAccessToken?.();

        await fetch(apiUrl(`/api/account/${uid}`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ language: resolved }),
        });
      } catch {}
    },
    [userId]
  );

  const toggleLanguage = useCallback(() => {
    setAndPersistLanguage(language === "uk" ? "en" : "uk");
  }, [language, setAndPersistLanguage]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: setAndPersistLanguage,
        toggleLanguage,
        loading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);